/**
 * The panels on an item page. Each mutation returns the item's new detail,
 * which replaces the cached one (useItemMutation).
 *
 *  - MoneyPanel: profit (estimated or realized), the pricing suggestion and
 *    the recorded costs.
 *  - ShipPanel: pick, pack, label, hand-off, once the item has sold.
 *  - SaleDialog / ListingDialog: opened from the header and the
 *    Marketplaces panel.
 */
import { useMemo, useState } from "react";
import type { CostCreate, ItemDetail, ListingUpsert, SaleCreate, SaleUpdate } from "@sable/api-contract";
import { COST_KINDS, PACKAGING, estimateProfit, floorPrice, marketplaceFee, type FeeProfile } from "@sable/domain";
import { IconCheck, IconPlus, IconTrash } from "../../components/icons.tsx";
import { packagingSummary } from "../../components/PackagingDialog.tsx";
import { Dialog, Field, MarketplaceBadge, MoneyInput } from "../../components/ui.tsx";
import { t, tk } from "../../i18n/index.ts";
import { request } from "../../lib/api.ts";
import { money, shortDate, todayIn } from "../../lib/format.ts";
import { useFees, useItemMutation, useMarketplaces, useSettings } from "../../lib/queries.ts";
import { useCan, useWs } from "../../lib/workspace.tsx";

export const IN_STOCK = ["draft", "in_stock", "listed"];

export function useMarketplaceName() {
  const mps = useMarketplaces();
  return (code: string) => mps.data?.find((m) => m.code === code)?.name ?? code;
}

/** The item's sale, unless it was returned. */
export function activeSale(item: ItemDetail) {
  return item.sale && !item.sale.returned ? item.sale : null;
}

// ---- Money (profit + pricing + costs) ---------------------------------------------

interface Estimate {
  marketplace: string;
  price: number | null;
  profit: number | null;
  floor: number | null;
  fees: number | null;
  purchase: number;
  packaging: number;
  /** Postage the seller pays: on a seller-pays marketplace, or when buyers don't pay it. */
  shipping: number;
}

function useEstimate(item: ItemDetail): Estimate | null {
  const ws = useWs();
  const fees = useFees(ws.id);
  const settings = useSettings(ws.id);
  return useMemo(() => {
    if (activeSale(item) || !fees.data || !settings.data) return null;
    const live = item.listings.find((l) => l.status === "active" && l.listPriceMinor !== null);
    const price = live?.listPriceMinor ?? item.profile?.suggestedListMinor ?? null;
    const marketplace = live?.marketplace ?? item.profile?.bestMarketplace ?? fees.data[0]?.marketplace;
    const fee = fees.data.find((f) => f.marketplace === marketplace);
    if (!fee) return null;
    const s = settings.data;
    const profile: FeeProfile = fee;
    const basis = {
      purchasePriceMinor: item.purchasePriceMinor ?? 0,
      packagingMinor: s.polyMailerCostMinor,
      shippingMinor: s.avgShippingCostMinor,
      buyerPaysShipping: s.buyerPaysShipping,
    };
    // The same rule estimateProfit applies, so the lines add up to its figure.
    const sellerShips = profile.sellerPaysShipping || !s.buyerPaysShipping;
    return {
      marketplace: fee.marketplace,
      price,
      profit: price !== null ? estimateProfit(price, profile, basis) : null,
      floor: floorPrice(profile, basis, s.minAcceptableProfitMinor),
      fees: price !== null ? marketplaceFee(price, profile) : null,
      purchase: basis.purchasePriceMinor,
      packaging: basis.packagingMinor,
      shipping: sellerShips ? basis.shippingMinor : 0,
    };
  }, [item, fees.data, settings.data]);
}

export function MoneyPanel({ item }: { item: ItemDetail }) {
  const canFinance = useCan("finance.read");
  const canCosts = useCan("sales.write");
  const estimate = useEstimate(item);
  const p = item.profile;
  const showPricing = !activeSale(item) && p !== null &&
    ((p.suggestedLowMinor !== null && p.suggestedHighMinor !== null) || p.suggestedListMinor !== null || Boolean(p.rationale));

  if (!canFinance && !canCosts && !showPricing) return null;
  return (
    <div className="ip-money">
      {canFinance && <ProfitCard item={item} estimate={estimate} />}
      {showPricing && <PricingCard item={item} floor={canFinance ? estimate?.floor ?? null : null} />}
      {canCosts && <CostsCard item={item} />}
    </div>
  );
}

function ProfitCard({ item, estimate }: { item: ItemDetail; estimate: Estimate | null }) {
  const name = useMarketplaceName();
  const c = item.currency;
  const sale = activeSale(item);

  if (sale) {
    const l = item.ledger;
    const tone = (l.netProfitMinor ?? 0) >= 0 ? "pos" : "neg";
    return (
      <section className="card stack" aria-labelledby="ip-profit">
        <div className="ip-figure">
          <h2 id="ip-profit" className="ip-label">{t("item.net")}</h2>
          <div className={`ip-big num ${tone}`}>{money(l.netProfitMinor, c)}</div>
          {l.daysToSale !== null && <p className="muted small">{t("item.daysToSale", { days: l.daysToSale })}</p>}
        </div>
        <dl className="ip-ledger">
          <dt>{t("item.revenue")}</dt><dd>{money(l.grossRevenueMinor, c)}</dd>
          <dt>{t("item.fees")}</dt><dd>−{money(l.platformFeeMinor + l.processingFeeMinor, c)}</dd>
          <dt>{t("item.cogs")}</dt><dd>−{money(item.purchasePriceMinor ?? 0, c)}</dd>
          <dt>{t("item.otherCosts")}</dt><dd>−{money(l.recordedCostsMinor, c)}</dd>
          <dt className="total">{t("item.net")}</dt>
          <dd className={`total ${tone}`}>{money(l.netProfitMinor, c)}</dd>
        </dl>
      </section>
    );
  }

  const e = estimate && estimate.price !== null && estimate.profit !== null ? estimate : null;
  const tone = e && e.profit! >= 0 ? "pos" : "neg";
  return (
    <section className="card stack" aria-labelledby="ip-profit">
      <div className="ip-figure">
        <h2 id="ip-profit" className="ip-label">{t("item.estimatedProfit")}</h2>
        {e ? (
          <>
            <div className={`ip-big num ${tone}`}>{money(e.profit, c)}</div>
            <p className="muted small">{t("item.estimateAt", { price: money(e.price, c), marketplace: name(e.marketplace) })}</p>
          </>
        ) : (
          <p className="muted small">{estimate?.floor != null ? t("item.floor", { price: money(estimate.floor, c) }) : t("item.noPricing")}</p>
        )}
      </div>
      {e && (
        <dl className="ip-ledger">
          <dt>{t("item.listPrice")}</dt><dd>{money(e.price, c)}</dd>
          {e.fees !== null && <><dt>{t("item.fees")}</dt><dd>−{money(e.fees, c)}</dd></>}
          <dt>{t("item.cogs")}</dt><dd>−{money(e.purchase, c)}</dd>
          <dt>{t("item.packaging")}</dt><dd>−{money(e.packaging, c)}</dd>
          {e.shipping > 0 && <><dt>{t("item.shipping")}</dt><dd>−{money(e.shipping, c)}</dd></>}
          <dt className="total">{t("item.net")}</dt>
          <dd className={`total ${tone}`}>{money(e.profit, c)}</dd>
        </dl>
      )}
      {e && e.floor !== null && <p className="muted small">{t("item.floor", { price: money(e.floor, c) })}</p>}
      {item.ledger.daysHeld !== null && <p className="muted small">{t("item.daysHeld", { days: item.ledger.daysHeld })}</p>}
    </section>
  );
}

/**
 * The suggested band, the floor and live prices on one line. An SVG with
 * percentage coordinates, so nothing needs an inline style (CSP style-src 'self').
 */
function PriceRange({ low, high, floor, suggested, live, currency }: {
  low: number;
  high: number;
  floor: number | null;
  suggested: number | null;
  live: { code: string; name: string; price: number }[];
  currency: string;
}) {
  const points = [low, high, ...live.map((x) => x.price)];
  if (floor !== null) points.push(floor);
  if (suggested !== null) points.push(suggested);
  const lo = Math.min(...points) * 0.85;
  const hi = Math.max(...points) * 1.08;
  const at = (v: number) => (hi === lo ? 50 : ((v - lo) / (hi - lo)) * 100);
  const pct = (v: number) => `${at(v)}%`;
  // Labels keep inside the box: anchored to whichever side has room.
  const anchor = (v: number) => (at(v) < 12 ? "start" : at(v) > 88 ? "end" : "middle");
  return (
    <>
      <svg className="ip-range" role="img" aria-label={t("item.suggested", { low: money(low, currency), high: money(high, currency) })}>
        <rect className="ip-range-track" x="0" y="14" width="100%" height="6" rx="3" />
        <rect className="ip-range-band" x={pct(low)} y="14" width={`${at(high) - at(low)}%`} height="6" rx="3" />
        {floor !== null && <line className="ip-range-floor" x1={pct(floor)} x2={pct(floor)} y1="8" y2="26" />}
        {suggested !== null && <circle className="ip-range-dot is-suggested" cx={pct(suggested)} cy="17" r="5" />}
        {live.map((x) => (
          <circle key={x.code} className="ip-range-dot" cx={pct(x.price)} cy="17" r="5"><title>{`${x.name} · ${money(x.price, currency)}`}</title></circle>
        ))}
        <text className="ip-range-tick" x={pct(low)} y="40" textAnchor={anchor(low)}>{money(low, currency)}</text>
        <text className="ip-range-tick" x={pct(high)} y="40" textAnchor={anchor(high)}>{money(high, currency)}</text>
        {floor !== null && <text className="ip-range-tick is-floor" x={pct(floor)} y="40" textAnchor={anchor(floor)}>{money(floor, currency)}</text>}
      </svg>
      <div className="ip-legend small">
        {live.length > 0 && <span><i className="ip-key-dot" />{t("item.legendLive")}</span>}
        {suggested !== null && <span><i className="ip-key-dot is-suggested" />{t("item.legendSuggested")}</span>}
        {floor !== null && <span><i className="ip-key-floor" />{t("item.legendFloor")}</span>}
      </div>
    </>
  );
}

function PricingCard({ item, floor }: { item: ItemDetail; floor: number | null }) {
  const p = item.profile!;
  const name = useMarketplaceName();
  const live = item.listings
    .filter((l) => l.status === "active" && l.listPriceMinor !== null)
    .map((l) => ({ code: l.marketplace, name: name(l.marketplace), price: l.listPriceMinor! }));

  return (
    <section className="card stack" aria-labelledby="ip-pricing">
      <div className="spread">
        <h2 id="ip-pricing" className="ip-label">{t("item.suggestedPrice")}</h2>
        {p.bestMarketplace && <span className="muted small">{t("item.bestOn", { marketplace: name(p.bestMarketplace) })}</span>}
      </div>
      {p.suggestedListMinor !== null && <div className="ip-mid num">{money(p.suggestedListMinor, p.currency)}</div>}
      {p.suggestedLowMinor !== null && p.suggestedHighMinor !== null && (
        <PriceRange low={p.suggestedLowMinor} high={p.suggestedHighMinor} floor={floor} suggested={p.suggestedListMinor} live={live} currency={p.currency} />
      )}
      {p.rationale && (
        <details className="ip-why">
          <summary>{t("item.whyPrice")}</summary>
          <p className="small">{p.rationale}</p>
        </details>
      )}
    </section>
  );
}

function CostsCard({ item }: { item: ItemDetail }) {
  const ws = useWs();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<(typeof COST_KINDS)[number]>("shipping_label");
  const [amount, setAmount] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const add = useItemMutation(ws.id, item.id, (body: CostCreate) =>
    request<ItemDetail>(`/items/${item.id}/costs`, { method: "POST", workspace: ws.id, body }));
  const remove = useItemMutation(ws.id, item.id, (costId: string) =>
    request<ItemDetail>(`/items/${item.id}/costs/${costId}`, { method: "DELETE", workspace: ws.id }));

  return (
    <section className="card stack" aria-labelledby="ip-costs">
      <div className="spread">
        <h2 id="ip-costs" className="ip-label">{t("item.costs")}</h2>
        <button className="btn btn-sm" onClick={() => setOpen(true)}><IconPlus size={14} />{t("item.addCost")}</button>
      </div>
      {item.costs.length === 0 ? (
        <p className="muted small">{t("item.noCosts")}</p>
      ) : (
        <ul className="ip-cost-list">
          {item.costs.map((k) => (
            <li key={k.id}>
              <div className="grow">
                <div>{tk("cost", k.kind)}{k.isEstimate && <span className="muted small"> · {t("item.estimate")}</span>}</div>
                <div className="muted small">{shortDate(k.incurredOn)}{k.note ? ` · ${k.note}` : ""}</div>
              </div>
              <span className="num">{money(k.amountMinor, k.currency)}</span>
              <button className="icon-btn" aria-label={t("app.delete")} disabled={remove.isPending} onClick={() => remove.mutate(k.id)}><IconTrash size={16} /></button>
            </li>
          ))}
        </ul>
      )}
      {remove.isError && <p className="form-error">{remove.error.message}</p>}
      {open && (
        <Dialog
          title={t("item.addCost")}
          onClose={() => setOpen(false)}
          footer={<>
            <button className="btn" onClick={() => setOpen(false)}>{t("app.cancel")}</button>
            <button className="btn btn-primary" disabled={add.isPending || amount === null}
              onClick={() => amount !== null && add.mutate({ kind, amountMinor: amount, note: note.trim() || null }, {
                onSuccess: () => { setOpen(false); setAmount(null); setNote(""); },
              })}>
              {add.isPending ? t("app.saving") : t("app.save")}
            </button>
          </>}
        >
          <div className="stack">
            <div className="grid-2">
              <Field label={t("item.costKind")}>
                <select className="select" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                  {COST_KINDS.map((k) => <option key={k} value={k}>{tk("cost", k)}</option>)}
                </select>
              </Field>
              <Field label={t("item.amount")}>
                <MoneyInput currency={item.currency} locale={ws.locale} value={amount} onChange={setAmount} required />
              </Field>
            </div>
            <Field label={t("item.note")} optional>
              <input className="input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
            </Field>
            {add.isError && <p className="form-error">{add.error.message}</p>}
          </div>
        </Dialog>
      )}
    </section>
  );
}

// ---- Listing dialog (listings entered by hand) ----------------------------------------

export function ListingDialog({ item, onClose, initial }: { item: ItemDetail; onClose: () => void; initial?: ItemDetail["listings"][number] }) {
  const ws = useWs();
  const mps = useMarketplaces();
  const name = useMarketplaceName();
  const taken = new Set(item.listings.map((l) => l.marketplace));
  const [marketplace, setMarketplace] = useState(initial?.marketplace ?? mps.data?.find((m) => !taken.has(m.code))?.code ?? "ebay");
  const [price, setPrice] = useState<number | null>(initial?.listPriceMinor ?? item.profile?.suggestedListMinor ?? null);
  const [url, setUrl] = useState(initial?.externalUrl ?? "");
  const save = useItemMutation(ws.id, item.id, (body: ListingUpsert) =>
    request<ItemDetail>(`/items/${item.id}/listings`, { method: "PUT", workspace: ws.id, body }));
  return (
    <Dialog
      title={initial ? name(initial.marketplace) : t("item.addListing")}
      onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>{t("app.cancel")}</button>
        <button className="btn btn-primary" disabled={save.isPending}
          onClick={() => save.mutate({ marketplace, listPriceMinor: price, externalUrl: url.trim() || null, status: "active" }, { onSuccess: onClose })}>
          {save.isPending ? t("app.saving") : t("app.save")}
        </button>
      </>}
    >
      <div className="stack">
        {!initial && (
          <Field label={t("item.marketplace")}>
            <select className="select" value={marketplace} onChange={(e) => setMarketplace(e.target.value)}>
              {mps.data?.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
            </select>
          </Field>
        )}
        <Field label={t("item.listPrice")}>
          <MoneyInput currency={item.currency} locale={ws.locale} value={price} onChange={setPrice} />
        </Field>
        <Field label={t("item.listingUrl")} optional>
          <input className="input" type="url" inputMode="url" value={url} placeholder="https://" onChange={(e) => setUrl(e.target.value)} />
        </Field>
        {save.isError && <p className="form-error">{save.error.message}</p>}
      </div>
    </Dialog>
  );
}

// ---- Sale ---------------------------------------------------------------------------

export function SaleDialog({ item, onClose }: { item: ItemDetail; onClose: () => void }) {
  const ws = useWs();
  const mps = useMarketplaces();
  const live = item.listings.find((l) => l.status === "active");
  const [marketplace, setMarketplace] = useState(live?.marketplace ?? "ebay");
  const [price, setPrice] = useState<number | null>(live?.listPriceMinor ?? null);
  const [shipping, setShipping] = useState<number | null>(0);
  const [label, setLabel] = useState<number | null>(null);
  const [date, setDate] = useState(todayIn(ws.timezone));
  const [orderId, setOrderId] = useState("");
  // The mailer defaults from the item's packaging: a box is "other", a polybag a poly mailer.
  const [packaging, setPackaging] = useState<(typeof PACKAGING)[number]>(item.packagingType === "box" ? "other" : "poly");
  const [boosted, setBoosted] = useState(false);
  const record = useItemMutation(ws.id, item.id, (body: SaleCreate) =>
    request<ItemDetail>(`/items/${item.id}/sale`, { method: "POST", workspace: ws.id, body }));

  const submit = () => {
    if (price === null) return;
    // Noon on the chosen day: the sale lands on that date in any nearby time zone.
    const soldAt = new Date(`${date}T12:00:00`).toISOString();
    record.mutate({
      marketplace, salePriceMinor: price, buyerShippingPaidMinor: shipping ?? 0, soldAt,
      externalOrderId: orderId.trim() || null, packaging, boosted, shippingLabelMinor: label,
    }, { onSuccess: onClose });
  };

  return (
    <Dialog
      title={t("item.recordSale")}
      onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>{t("app.cancel")}</button>
        <button className="btn btn-accent" disabled={record.isPending || price === null} onClick={submit}>
          {record.isPending ? t("app.saving") : t("item.recordSale")}
        </button>
      </>}
    >
      <div className="stack">
        <div className="grid-2">
          <Field label={t("item.marketplace")}>
            <select className="select" value={marketplace} onChange={(e) => setMarketplace(e.target.value)}>
              {mps.data?.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
            </select>
          </Field>
          <Field label={t("item.soldOn")}>
            <input className="input" type="date" value={date} max={todayIn(ws.timezone)} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label={t("item.salePrice")}>
            <MoneyInput currency={item.currency} locale={ws.locale} value={price} onChange={setPrice} required />
          </Field>
          <Field label={t("item.buyerShipping")}>
            <MoneyInput currency={item.currency} locale={ws.locale} value={shipping} onChange={setShipping} />
          </Field>
          <Field label={t("item.shippingLabel")} optional>
            <MoneyInput currency={item.currency} locale={ws.locale} value={label} onChange={setLabel} />
          </Field>
          <Field label={t("item.packaging")}>
            <select className="select" value={packaging} onChange={(e) => setPackaging(e.target.value as typeof packaging)}>
              {PACKAGING.map((p) => <option key={p} value={p}>{tk("packaging", p)}</option>)}
            </select>
          </Field>
        </div>
        <Field label={t("item.orderId")} optional>
          <input className="input" value={orderId} maxLength={120} onChange={(e) => setOrderId(e.target.value)} />
        </Field>
        <label className="check">
          <input type="checkbox" checked={boosted} onChange={(e) => setBoosted(e.target.checked)} />
          {t("item.boosted")}
        </label>
        {record.isError && <p className="form-error">{record.error.message}</p>}
      </div>
    </Dialog>
  );
}

function Step({ n, done, title, children, action }: { n: number; done: boolean; title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <li className={`ip-step${done ? " is-done" : ""}`}>
      <span className="ip-step-num">{done ? <IconCheck size={14} /> : n}</span>
      <div className="ip-step-body">
        <div className="ip-step-title">{title}</div>
        {children}
      </div>
      {action}
    </li>
  );
}

/**
 * Once sold: everything needed to get the parcel out the door, in the order
 * it happens. Returned / undo sit at the bottom, out of the way.
 */
export function ShipPanel({ item }: { item: ItemDetail }) {
  const ws = useWs();
  const canSell = useCan("sales.write");
  const mpName = useMarketplaceName();
  const sale = activeSale(item);
  const [tracking, setTracking] = useState(sale?.trackingNumber ?? "");
  const [labelCost, setLabelCost] = useState<number | null>(null);
  const update = useItemMutation(ws.id, item.id, (body: SaleUpdate) =>
    request<ItemDetail>(`/sales/${item.sale!.id}`, { method: "PATCH", workspace: ws.id, body }));
  const undo = useItemMutation(ws.id, item.id, () =>
    request<ItemDetail>(`/sales/${item.sale!.id}`, { method: "DELETE", workspace: ws.id }));
  const addCost = useItemMutation(ws.id, item.id, (body: CostCreate) =>
    request<ItemDetail>(`/items/${item.id}/costs`, { method: "POST", workspace: ws.id, body }));

  if (!canSell || !sale) return null;
  const shipped = Boolean(sale.shippedAt);
  const label = item.costs.find((k) => k.kind === "shipping_label");
  const pack = packagingSummary(item);
  const err = update.error ?? undo.error ?? addCost.error;

  return (
    <section className="card" aria-labelledby="ip-ship">
      <div className="ip-card-head">
        <div>
          <h2 id="ip-ship">{shipped ? t("item.ship.titleDone") : t("item.ship.title")}</h2>
          <p className="muted small">
            {mpName(sale.marketplace)} · {money(sale.salePriceMinor, sale.currency)} · {shortDate(sale.soldAt)}
            {sale.externalOrderId ? ` · #${sale.externalOrderId}` : ""}
          </p>
        </div>
        <MarketplaceBadge code={sale.marketplace} />
      </div>

      <ol className="ip-steps">
        <Step n={1} done={Boolean(item.storageBin)} title={t("item.ship.pick")}>
          {item.storageBin
            ? <div className="row"><span className="muted">{t("item.ship.fromBin")}</span><span className="mono ip-bin">{item.storageBin}</span></div>
            : <span className="muted small">{t("item.ship.noBin")}</span>}
        </Step>
        <Step n={2} done={Boolean(pack)} title={t("item.ship.pack")}>
          <span className={pack ? "" : "muted small"}>{pack ?? t("item.ship.noPackaging")}</span>
        </Step>
        <Step n={3} done={Boolean(label)} title={t("item.ship.label")}>
          <div className="ip-ship-fields">
            <div className="ip-field">
              <span className="ip-label">{t("item.ship.labelCost")}</span>
              {label ? (
                <span className="num ip-field-value">{money(label.amountMinor, label.currency)}</span>
              ) : (
                <div className="row">
                  <MoneyInput currency={item.currency} locale={ws.locale} value={labelCost} onChange={setLabelCost} />
                  <button className="btn btn-sm" disabled={labelCost === null || addCost.isPending}
                    onClick={() => labelCost !== null && addCost.mutate({ kind: "shipping_label", amountMinor: labelCost, note: null }, { onSuccess: () => setLabelCost(null) })}>
                    {t("app.save")}
                  </button>
                </div>
              )}
            </div>
            <label className="ip-field grow">
              <span className="ip-label">{t("item.ship.trackingOptional")}</span>
              <span className="row">
                <input className="input mono grow" placeholder={t("item.tracking")} value={tracking} maxLength={80} onChange={(e) => setTracking(e.target.value)} />
                <button className="btn btn-sm" disabled={update.isPending || tracking === (sale.trackingNumber ?? "")}
                  onClick={() => update.mutate({ trackingNumber: tracking.trim() || null })}>{t("app.save")}</button>
              </span>
            </label>
          </div>
        </Step>
        <Step n={4} done={shipped} title={t("item.ship.handOff")} action={
          <button className={`btn btn-sm${shipped ? "" : " btn-primary"}`} disabled={update.isPending} onClick={() => update.mutate({ shipped: !shipped })}>
            {shipped ? t("item.markNotShipped") : t("item.markShipped")}
          </button>
        }>
          <span className="muted small">{shipped ? t("item.shippedOn", { date: shortDate(sale.shippedAt!) }) : t("item.ship.handOffHint")}</span>
        </Step>
      </ol>

      <div className="ip-card-foot ip-foot-end">
        <button className="btn btn-sm btn-ghost" disabled={update.isPending} onClick={() => update.mutate({ returned: true })}>{t("item.markReturned")}</button>
        <button className="btn btn-sm btn-ghost ip-danger-text" disabled={undo.isPending}
          onClick={() => { if (confirm(t("item.undoSaleConfirm"))) undo.mutate(undefined); }}>{t("item.undoSale")}</button>
      </div>
      {err && <p className="form-error">{err.message}</p>}
    </section>
  );
}
